import { usePersistedState } from '../util/persistedState';
import { useAsync } from 'react-use';
import { useEffect } from 'react';
import { PromiseType } from 'react-use/lib/util';
import { Duration } from 'date-fns';
import { ConfigHelper } from '@devxdiscover/devhub-common';

/**
 * An IOP incident overview
 */
export type IncidentOverview = {
  numApps: number;
  incidents: Incident[];
  summary: IncidentSummary[];
};
/**
 * A summary of the number of incidents by severity.
 */
export type IncidentSummary = {
  severity: number;
  count: number;
  message: string;
};

/**
 * A single incident
 */
export type Incident = {
  appName: string;
  appId: number;
  incidentNumber: string;
  category: string;
  severity: number;
  startTime: string;
  sysId: string;
  uSolutionCode: string;
  message: string;
};

/**
 * An IOP user with associated roles.
 */
type IOPRolesResult = {
  user: string;
  roles: IOPRole[];
};

/**
 * A single IOP role
 */
type IOPRole = {
  appid: number;
  appname: string;
  assetType: string;
  roleid: number;
  rolename: string;
};

/**
 * Retrieves the IOP incidents for the user provided by the given token.
 * @param cacheDuration - The {@link Duration} after which the state should be considered expired.
 */
export const useIOPIncidentRequest: (cacheDuration?: Duration) => {
  loading: boolean;
  error?: Error;
  value?: PromiseType<Promise<IncidentOverview>>;
} = (cacheDuration: Duration = { minutes: 60 }) => {
  const incidentOverview = {
    numApps: 0,
    incidents: [],
    summary: [],
  } as IncidentOverview;

  const [foundIncidents, setFoundIncidents] =
    usePersistedState<IncidentOverview>(
      'iop-incidents',
      cacheDuration,
      incidentOverview,
    );

  const { value, loading, error } = useAsync(
    async (): Promise<IncidentOverview> => {
      if (
        foundIncidents?.numApps === 0 &&
        (foundIncidents?.incidents?.length ?? 0) === 0
      ) {
        const appIds = await retrieveAppIdList();

        const appResponse = await fetch(
          ConfigHelper.instance.proxyUrl(
            `/obiee/getIOPIncidentsByApps/${appIds}`,
          ).href,
        );
        const incidents: Incident[] = await appResponse.json();

        // Convert the severity to a number - it comes back as a string.
        incidents.forEach(incident => {
          incident.severity = parseInt(incident.severity.toString(), 10);
        });

        incidentOverview.numApps = appIds.split(',').length;
        incidentOverview.incidents = incidents;
        incidentOverview.summary = buildIncidentSummary(incidents);
        return incidentOverview;
      }
      return foundIncidents;
    },
  );

  useEffect(() => {
    if (value) {
      setFoundIncidents(value);
    }
  }, [value, setFoundIncidents]);

  return {
    value,
    loading,
    error,
  };
};

/**
 * Retrieves the list of application IDs for the user as a comma-delimited string.
 */
export async function retrieveAppIdList(): Promise<string> {
  const response = await fetch(
    ConfigHelper.instance.proxyUrl(`/data-service/roles/api/v1/roles`).href,
  );
  const rolesResult: IOPRolesResult = await response.json();
  const appIds = new Set<number>();
  const validRoles = ['Developer', 'Stakeholder', 'iTRC Approver'];
  if (rolesResult.roles.length > 0) {
    const filteredRoles = rolesResult.roles;
    filteredRoles
      .filter(item => validRoles.includes(item.rolename))
      .forEach(newObj => {
        appIds.add(newObj.appid);
      });
  }
  return Array.from(appIds).join(',');
}

/**
 * Returns a message based on incident severity
 */
export const severityMessages: { [key: number]: string } = {
  5: 'Planning incident',
  4: 'Low severity incident',
  3: 'Low severity incident',
  2: 'Medium severity incident',
  1: 'High severity incident',
};

/**
 * Builds a summary of severity for the given incidents.
 * @param incidents - The incidents to summarize.
 */
function buildIncidentSummary(incidents: Incident[]): IncidentSummary[] {
  const summary: IncidentSummary[] = [];
  for (let i = 1; i < 6; i++) {
    const incidentSummary: IncidentSummary = {
      severity: i,
      count: incidents.filter(item => item.severity === i).length,
      message: severityMessages[i],
    };

    if (incidentSummary.count > 0) {
      summary.push({
        severity: incidentSummary.severity,
        count: incidentSummary.count,
        message: incidentSummary.message,
      });
    }
  }

  return summary;
}
